import { create } from 'zustand'
import api from '../services/api'
import { usePermissionStore } from './permissionStore'

interface User {
  user_id: string
  email: string
  username: string
  user_type: 'superadmin' | 'tenant_user'
  tenant_id?: string
  tenant_code?: string
  tenant_name?: string
  role: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  mustChangePassword: boolean

  login: (email: string, password: string, tenantCode?: string) => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => void
  clearMustChangePassword: () => void
}

/**
 * Restituisce il tenant_code salvato in localStorage.
 * Persiste anche dopo logout/expiry, cosi' l'utente viene rediretto
 * alla login del tenant corretto invece della landing generica.
 */
export function getSavedTenantCode(): string | null {
  return localStorage.getItem('erp_tenant_code')
}

export const useAuthStore = create<AuthState>((set) => ({
  user: JSON.parse(localStorage.getItem('erp_user') || 'null'),
  token: localStorage.getItem('erp_token'),
  isAuthenticated: !!localStorage.getItem('erp_token'),
  isLoading: false,
  error: null,
  mustChangePassword: false,

  login: async (email: string, password: string, tenantCode?: string) => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.post('/v1/auth/login', {
        email,
        password,
        tenant_code: tenantCode || null,
      })

      const data = response.data
      const user: User = {
        user_id: data.user_id,
        email: data.email,
        username: data.username,
        user_type: data.user_type,
        tenant_id: data.tenant_id,
        tenant_code: data.tenant_code,
        tenant_name: data.tenant_name,
        role: data.role,
      }

      localStorage.setItem('erp_token', data.access_token)
      localStorage.setItem('erp_user', JSON.stringify(user))

      // Salva tenant_code separatamente — sopravvive a logout/expiry
      if (data.tenant_code) {
        localStorage.setItem('erp_tenant_code', data.tenant_code)
      }

      set({
        user,
        token: data.access_token,
        isAuthenticated: true,
        isLoading: false,
        mustChangePassword: !!data.must_change_password,
      })

      // Carica permessi dopo login
      if (data.user_type === 'tenant_user') {
        usePermissionStore.getState().loadPermissions()
      }

      return true
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Errore di autenticazione'
      set({ error: message, isLoading: false })
      return false
    }
  },

  logout: async () => {
    // Chiama API logout per invalidare la sessione sul server
    try {
      await api.post('/v1/auth/logout')
    } catch (error) {
      // Ignora errori - procedi comunque con logout locale
      console.warn('Errore durante logout API:', error)
    }

    // NON rimuoviamo erp_tenant_code — serve per redirigere alla login giusta
    localStorage.removeItem('erp_token')
    localStorage.removeItem('erp_user')
    usePermissionStore.getState().clearPermissions()
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      mustChangePassword: false,
    })
  },

  clearMustChangePassword: () => set({ mustChangePassword: false }),

  checkAuth: () => {
    const token = localStorage.getItem('erp_token')
    const user = JSON.parse(localStorage.getItem('erp_user') || 'null')

    if (token && user) {
      set({ token, user, isAuthenticated: true })
    } else {
      set({ token: null, user: null, isAuthenticated: false })
    }
  },
}))
